import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useCollection } from '../hooks/useFirestore'
import { useAudioPlayer } from '../hooks/useAudioPlayer'
import AlbumArtViewer from '../components/albums/AlbumArtViewer'
import './AlbumsPage.css'

// Placeholder album art
const PLACEHOLDER_ART = 'data:image/svg+xml,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <rect fill="#1E1A22" width="100" height="100"/>
    <circle cx="50" cy="50" r="30" fill="none" stroke="#6E2EBF" stroke-width="2"/>
    <circle cx="50" cy="50" r="10" fill="#6E2EBF"/>
  </svg>
`)

// Get primary cover: images.front first, then legacy coverArtUrl
function getCoverArt(album) {
  return album.images?.front || album.coverArtUrl || PLACEHOLDER_ART
}

// Convert image URL to thumbnail URL (adds _thumb before extension)
function getThumbnailUrl(url) {
  if (!url || url.startsWith('data:')) return url

  // Find the last dot for extension
  const lastDot = url.lastIndexOf('.')
  const queryStart = url.indexOf('?')

  if (lastDot === -1) return url

  // Handle URLs with query strings (Firebase Storage tokens)
  if (queryStart !== -1 && queryStart > lastDot) {
    const base = url.slice(0, lastDot)
    const rest = url.slice(lastDot)
    // Insert _thumb before extension: .jpg?token -> _thumb.jpg?token
    return `${base}_thumb${rest}`
  }

  // Simple URL without query string
  const base = url.slice(0, lastDot)
  const ext = url.slice(lastDot)
  return `${base}_thumb${ext}`
}

// Get thumbnail for album cover display
function getCoverThumbnail(album) {
  const fullUrl = getCoverArt(album)
  return getThumbnailUrl(fullUrl)
}

export default function AlbumsPage() {
  const { data: albums, loading: albumsLoading } = useCollection('albums', { orderByField: 'sortOrder' })
  const { data: songs } = useCollection('songs')
  const { data: characters } = useCollection('characters')
  const { currentSong, isPlaying, playSong, togglePlay } = useAudioPlayer()
  const [viewerAlbum, setViewerAlbum] = useState(null)

  // Find character by name (case-insensitive)
  const findCharacterByName = (name) => {
    if (!name) return null
    return characters.find(c => c.name?.toLowerCase() === name.toLowerCase())
  }

  // Group songs by album
  const songsByAlbum = songs.reduce((acc, song) => {
    if (!acc[song.albumId]) acc[song.albumId] = []
    acc[song.albumId].push(song)
    return acc
  }, {})

  // Sort songs by track number
  Object.keys(songsByAlbum).forEach(albumId => {
    songsByAlbum[albumId].sort((a, b) => (a.trackNumber || 0) - (b.trackNumber || 0))
  })

  if (albumsLoading) {
    return (
      <div className="container">
        <div className="albums-loading">
          <div className="loading-spinner" />
          <p>Loading albums...</p>
        </div>
      </div>
    )
  }

  if (albums.length === 0) {
    return (
      <div className="container">
        <h1>Albums</h1>
        <div className="albums-empty">
          <p>No albums yet.</p>
          <p className="text-secondary">Add albums from the Admin section.</p>
        </div>
      </div>
    )
  }

  const handlePlaySong = (song, album) => {
    const albumSongs = songsByAlbum[album.id] || []
    if (currentSong?.id === song.id) {
      togglePlay()
    } else {
      playSong(song, album, albumSongs)
    }
  }

  const formatDuration = (seconds) => {
    if (!seconds) return '--:--'
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="container">
      <h1>Albums</h1>

      <div className="albums-list">
        {albums.map(album => {
          const albumSongs = songsByAlbum[album.id] || []

          return (
            <div key={album.id} className="album-section">
              <div className="album-header">
                <div className="album-cover" onClick={() => setViewerAlbum(album)}>
                  <img
                    src={getCoverThumbnail(album)}
                    alt={album.title}
                    onError={(e) => {
                      // Fallback to full image if thumbnail doesn't exist, then placeholder
                      if (!e.target.dataset.triedFull) {
                        e.target.dataset.triedFull = 'true'
                        e.target.src = getCoverArt(album)
                      } else {
                        e.target.src = PLACEHOLDER_ART
                      }
                    }}
                  />
                </div>
                <div className="album-info">
                  <h2>{album.title}</h2>
                  <p className="album-artist">
                    {(() => {
                      const artistChar = findCharacterByName(album.artist)
                      return artistChar ? (
                        <Link to={`/characters/${artistChar.id}`}>{album.artist}</Link>
                      ) : album.artist
                    })()}
                  </p>
                  {album.copyrightYear && (
                    <p className="album-year">© {album.copyrightYear}</p>
                  )}
                  <p className="album-tracks">{albumSongs.length} tracks</p>
                </div>
              </div>

              {albumSongs.length > 0 ? (
                <div className="song-list">
                  <div className="song-list-header">
                    <span className="song-num">#</span>
                    <span className="song-title">Title</span>
                    <span className="song-duration">Duration</span>
                    <span className="song-download">Download</span>
                  </div>
                  {albumSongs.map((song, index) => {
                    const isCurrentSong = currentSong?.id === song.id
                    const isThisPlaying = isCurrentSong && isPlaying
                    const hasLyrics = !!song.lyrics?.trim()

                    return (
                      <div key={song.id} className="song-item">
                        <div
                          className={`song-row ${isCurrentSong ? 'active' : ''}`}
                        >
                          <span className="song-num">
                            <button
                              className="play-button"
                              onClick={() => handlePlaySong(song, album)}
                              title={isThisPlaying ? 'Pause' : 'Play'}
                            >
                              {isThisPlaying ? '⏸' : '▶'}
                            </button>
                            <span className="track-num">{song.trackNumber || index + 1}</span>
                          </span>
                          <span className="song-title">
                            {song.title}
                            {song.artist && song.artist !== album.artist && (
                              <em className="song-artist">
                                {' – '}
                                {(() => {
                                  const songArtistChar = findCharacterByName(song.artist)
                                  return songArtistChar ? (
                                    <Link to={`/characters/${songArtistChar.id}`}>{song.artist}</Link>
                                  ) : song.artist
                                })()}
                              </em>
                            )}
                          </span>
                          <span className="song-duration">{formatDuration(song.durationSeconds)}</span>
                          <span className="song-download">
                            {song.wavDownloadUrl && (
                              <a
                                href={song.wavDownloadUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="download-link"
                                title="Download WAV"
                              >
                                WAV
                              </a>
                            )}
                          </span>
                        </div>
                        {(isCurrentSong && hasLyrics) && (
                          <div className="song-lyrics">
                            <pre>{song.lyrics}</pre>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <p className="no-songs">No songs in this album yet.</p>
              )}
            </div>
          )
        })}
      </div>

      {viewerAlbum && (
        <AlbumArtViewer
          album={viewerAlbum}
          isOpen={!!viewerAlbum}
          onClose={() => setViewerAlbum(null)}
        />
      )}
    </div>
  )
}
